/**
 * Numbers for the public results page (/s/<token>), built the way the
 * Overview builds its growth curve: each strategy's equity curve windowed to
 * the period and rebased to the starting capital (utils/strategyWindow.js:
 * Kairos-style equity curves compound, futures journals add $ P&L).
 * Every stat below is read off those growth curves.
 */
import { windowPortfolioBlendSeries, windowStrategyPoints } from './strategyWindow';

export const RANGES = ['1M', '3M', '6M', 'YTD', '1Y', 'All'];
export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const toPoints = (rows) => (rows || []).map(([date, equity]) => ({ date, equity }));

/**
 * A curve's points in a view: live from the live-since date (keeping the last
 * point before it as the starting value), backtested up to it, or all. A
 * strategy with no live-since date is whole in Live and Full, absent in Backtested.
 */
export function pointsInView(points, liveSince, view) {
  if (!liveSince) return view === 'backtested' ? [] : points;
  if (view === 'full') return points;
  if (view === 'backtested') return points.filter((p) => p.date <= liveSince);
  const firstLive = points.findIndex((p) => p.date >= liveSince);
  if (firstLive < 0) return [];
  return points.slice(Math.max(0, firstLive - 1));
}

/** Last date with results across the chosen strategies in a view. */
export function lastDate(strategies, view) {
  let last = null;
  strategies.forEach((s) => {
    const pts = pointsInView(toPoints(s.points), s.liveSince, view);
    const d = pts[pts.length - 1]?.date;
    if (d && (!last || d > last)) last = d;
  });
  return last;
}

/**
 * First day of a range, counted back from today like the Overview's date range
 * (so "6M" means the same window on both), or null for All.
 */
export function rangeStart(range, today = new Date().toISOString().slice(0, 10)) {
  if (!today || range === 'All') return null;
  const d = new Date(`${today}T12:00:00Z`);
  if (range === 'YTD') return `${d.getUTCFullYear()}-01-01`;
  const months = { '1M': 1, '3M': 3, '6M': 6, '1Y': 12 }[range];
  if (!months) return null;
  d.setUTCMonth(d.getUTCMonth() - months);
  return d.toISOString().slice(0, 10);
}

/** One strategy's growth of `capital` in a view and period: [{ date, value }]. */
export function strategyGrowth(strategy, view, startDate, capital) {
  if (strategy.portfolioBuild && (view === 'full' || !strategy.liveSince)) {
    return windowPortfolioBlendSeries(strategy.portfolioBuild, startDate, capital);
  }
  const pts = pointsInView(toPoints(strategy.points), strategy.liveSince, view);
  return windowStrategyPoints(pts, startDate, capital, strategy.model || 'fixed', strategy.contracts || 1, strategy.base || null);
}

/**
 * Several strategies together: the capital split equally between them, each
 * grown on its share, added up by date (a strategy's last value carries over
 * days it has none).
 */
export function combinedGrowth(strategies, view, startDate, capital) {
  const list = strategies
    .map((s) => strategyGrowth(s, view, startDate, capital / strategies.length))
    .filter((pts) => pts.length >= 2);
  if (!list.length) return [];
  if (list.length === 1) return list[0].map((p) => ({ date: p.date, value: p.value * strategies.length }));
  const dates = [...new Set(list.flatMap((pts) => pts.map((p) => p.date)))].sort();
  const share = capital / strategies.length;
  const idx = list.map(() => 0);
  const lastVal = list.map(() => share);
  return dates.map((date) => {
    let sum = 0;
    list.forEach((pts, i) => {
      while (idx[i] < pts.length && pts[idx[i]].date <= date) { lastVal[i] = pts[idx[i]].value; idx[i] += 1; }
      sum += lastVal[i];
    });
    return { date, value: Math.round(sum * 100) / 100 };
  });
}

/** Month and year returns from a growth curve (month-end over the previous month-end). */
export function monthlyReturns(values) {
  const byYear = {};
  if (values.length < 2) return { byYear };
  let prevMonthEnd = values[0].value;
  let prevYearEnd = values[0].value;
  for (let i = 1; i < values.length; i += 1) {
    const { date, value } = values[i];
    const next = values[i + 1];
    const y = date.slice(0, 4);
    const m = Number(date.slice(5, 7)) - 1;
    if (!byYear[y]) byYear[y] = { months: {}, total: null };
    if (!next || next.date.slice(0, 7) !== date.slice(0, 7)) {
      byYear[y].months[m] = prevMonthEnd ? value / prevMonthEnd - 1 : 0;
      prevMonthEnd = value;
    }
    if (!next || next.date.slice(0, 4) !== y) {
      byYear[y].total = prevYearEnd ? value / prevYearEnd - 1 : 0;
      prevYearEnd = value;
    }
  }
  return { byYear };
}

/** Headline numbers from a growth curve that starts at `capital`. */
export function curveStats(values, capital) {
  const cap = Number(capital) || 100000;
  let peak = values[0]?.value ?? cap;
  let maxDd = 0;
  let maxDdPct = 0;
  let up = 0;
  let moved = 0;
  let gains = 0;
  let losses = 0;
  for (let i = 1; i < values.length; i += 1) {
    const change = values[i].value - values[i - 1].value;
    if (Math.abs(change) >= 0.005) {
      moved += 1;
      if (change > 0) { up += 1; gains += change; } else losses -= change;
    }
    peak = Math.max(peak, values[i].value);
    const dd = values[i].value - peak;
    if (dd < maxDd) maxDd = dd;
    if (peak > 0 && dd / peak < maxDdPct) maxDdPct = dd / peak;
  }
  const end = values[values.length - 1]?.value ?? cap;
  const months = Object.values(monthlyReturns(values).byYear).flatMap((y) => Object.values(y.months));
  return {
    net: end - cap,
    returnPct: end / cap - 1,
    maxDrawdown: maxDd,
    maxDrawdownPct: maxDdPct,
    winRate: moved ? up / moved : null,
    profitFactor: losses ? gains / losses : null,
    tradingDays: moved,
    from: values[1]?.date || values[0]?.date || null,
    to: values[values.length - 1]?.date || null,
    bestMonth: months.length ? Math.max(...months) : null,
    worstMonth: months.length ? Math.min(...months) : null,
    positiveMonths: months.length ? months.filter((v) => v > 0).length / months.length : null,
  };
}

/**
 * Benchmark values on the strategy's dates (last close carried forward), so
 * both lines share one axis. series: [{ date, normalized }] from the public benchmark endpoint.
 */
export function alignBenchmark(points, series) {
  if (!series?.length || !points.length) return points.map((p) => ({ ...p, bench: null }));
  const sorted = [...series].sort((a, b) => (a.date < b.date ? -1 : 1));
  let i = 0;
  let last = null;
  return points.map((p) => {
    while (i < sorted.length && sorted[i].date <= p.date) { last = sorted[i].normalized; i += 1; }
    return { ...p, bench: last };
  });
}
